import { useCallback } from 'react'
import { router } from '@inertiajs/react'
import { WhenVisible } from '@inertiajs/react'
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  type ColumnDef,
} from '@tanstack/react-table'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/Table'
import { VisitorAvatar } from '@/components/ui/VisitorAvatar'
import { getCountryFlag, getCountryName } from '@/lib/countries'
import { formatTimeAgo } from '@/lib/dateUtils'
import { useRouteParams } from '@/hooks/useRoutes'
import routes from '@/routeHelpers'
import { CopyableText } from '@/components/ui/CopyableText'
import type Visitor from '@/types/serializers/Visitor'
import type { Pagy, Filters } from './types'

interface ContactsTableProps {
  contacts: Visitor[]
  pagy: Pagy
  filters: Filters
}

const columns: ColumnDef<Visitor>[] = [
  {
    accessorKey: 'name',
    header: 'Name',
    cell: ({ row }) => (
      <div className="flex items-center gap-3">
        <VisitorAvatar
          name={row.original.name}
          identified={row.original.identified}
          active={row.original.active}
          live={row.original.live}
          countryCode={row.original.country_code}
          size="md"
        />
        <div className="min-w-0">
          <div className="font-medium truncate">{row.original.name}</div>
          {row.original.email && (
            <CopyableText
              text={row.original.email}
              className="text-xs text-muted-foreground max-w-full"
              toastMessage="Email copied to clipboard"
            />
          )}
        </div>
      </div>
    ),
  },
  {
    accessorKey: 'country_code',
    header: 'Location',
    cell: ({ row }) => {
      const flag = getCountryFlag(row.original.country_code)
      const name = getCountryName(row.original.country_code)
      if (!flag && !name) return <span className="text-muted-foreground">-</span>
      return (
        <span className="flex items-center gap-1.5">
          {flag && <span>{flag}</span>}
          <span className="text-sm">{name}</span>
        </span>
      )
    },
  },
  {
    accessorKey: 'conversations_count',
    header: 'Conversations',
    cell: ({ row }) => (
      <span className="text-sm tabular-nums">{row.original.conversations_count}</span>
    ),
  },
  {
    accessorKey: 'last_seen_at',
    header: 'Last Active',
    cell: ({ row }) => (
      <span className="text-sm text-muted-foreground">
        {row.original.last_seen_at ? formatTimeAgo(row.original.last_seen_at) : '-'}
      </span>
    ),
  },
]

export function ContactsTable({ contacts, pagy, filters }: ContactsTableProps) {
  const { withAccount } = useRouteParams()

  const table = useReactTable({
    data: contacts,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => row.id,
  })

  const handleRowClick = useCallback((contactId: string) => {
    router.visit(routes.contacts.show.path(withAccount({ id: contactId })), {
      preserveState: true,
    })
  }, [withAccount])

  if (contacts.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center p-12 text-center">
        <div className="max-w-sm">
          <p className="text-muted-foreground">
            {filters.search || filters.has_email || filters.has_conversations
              ? 'No contacts match your search.'
              : 'No contacts yet. Contacts appear when visitors interact with your widget.'}
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex-1 overflow-auto">
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableHead key={header.id} className="sticky top-0 bg-card z-10">
                  {header.isPlaceholder
                    ? null
                    : flexRender(header.column.columnDef.header, header.getContext())}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.map((row) => (
            <TableRow
              key={row.id}
              onClick={() => handleRowClick(row.original.id)}
              className="cursor-pointer"
            >
              {row.getVisibleCells().map((cell) => (
                <TableCell key={cell.id}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </TableRow>
          ))}

          {/* Infinite scroll trigger */}
          {pagy.page < pagy.last && (
            <TableRow>
              <TableCell colSpan={columns.length}>
                <WhenVisible
                  buffer={800}
                  params={{
                    data: {
                      page: pagy.page + 1,
                      search: filters.search || undefined,
                      has_email: filters.has_email || undefined,
                      has_conversations: filters.has_conversations || undefined,
                      sort: filters.sort,
                      order: filters.order,
                    },
                    only: ['contacts', 'pagy'],
                    preserveUrl: true,
                  }}
                  fallback={
                    <div className="flex justify-center py-4">
                      <div className="size-5 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                    </div>
                  }
                >
                  <div />
                </WhenVisible>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  )
}
